import { action, observable } from 'mobx';
import { ZipApiError } from './ZipApiError';
import { GraphAuthTimeoutError } from './GraphAuthTimeoutError';

export { GraphAuthTimeoutError };

export type GraphAuthStatus = 'idle' | 'pending' | 'timedOut';

export const INITIAL_DELAY_MS = 5000;
export const MAX_DELAY_MS = 30000;
export const BACKOFF_FACTOR = 1.5;
export const TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Detect the "Graph not yet authorized" 401 returned by data-syncs via lothlorien.
 *
 * Fires when new credentials are created in Turnstile and Graph doesn't see them yet
 * (cached on either Turnstile or Graph side). The body carries `Graph unauthorized` in
 * either the `message` or `error` field. This predicate distinguishes that transient
 * state from a genuine credential failure so callers can poll instead of surfacing
 * an error.
 */
export async function isGraphAuthPendingError(error: unknown): Promise<boolean> {
  if (!(error instanceof ZipApiError) || !error.response) {
    return false;
  }
  if (error.response.status !== 401) {
    return false;
  }
  try {
    const body = await error.response.clone().json();
    const detail = body?.message || body?.error;
    return typeof detail === 'string' && detail.includes('Graph unauthorized');
  } catch {
    return false;
  }
}

interface Waiter {
  resolve: () => void;
  reject: (error: Error) => void;
}

interface GraphAuthEntry {
  waiters: Waiter[];
  delay: number;
  startedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  check: () => Promise<unknown>;
}

export class GraphAuthStore {
  private entries = new Map<string, GraphAuthEntry>();
  @observable private statuses = observable.map<string, GraphAuthStatus>();

  /**
   * Observable status for the given instance: `idle` (no active poll), `pending` (polling),
   * or `timedOut` (last poll loop hit `TIMEOUT_MS` or failed with a non-pending error).
   */
  public getStatus(instanceId: string): GraphAuthStatus {
    return this.statuses.get(instanceId) ?? 'idle';
  }

  /**
   * Queue a caller to await Graph auth becoming ready for `instanceId`.
   *
   * The first caller per instance starts a polling loop that invokes `check` on an
   * exponential backoff (`INITIAL_DELAY_MS` → `MAX_DELAY_MS` × `BACKOFF_FACTOR`).
   * Additional callers on the same instance piggy-back on the in-flight loop and share its
   * outcome — `check` is therefore only ever called for the first caller.
   *
   * Resolves when `check` succeeds (Graph auth is ready). Rejects with
   * `GraphAuthTimeoutError` after `TIMEOUT_MS` of continuous pending responses, or with the
   * original error if `check` throws anything other than a Graph-pending 401.
   */
  public waitForGraphAuth(instanceId: string, check: () => Promise<unknown>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let entry = this.entries.get(instanceId);
      if (!entry) {
        entry = {
          waiters: [],
          delay: INITIAL_DELAY_MS,
          startedAt: Date.now(),
          timer: null,
          check
        };
        this.entries.set(instanceId, entry);
        this.setStatus(instanceId, 'pending');
        this.scheduleNext(instanceId);
      }
      entry.waiters.push({ resolve, reject });
    });
  }

  /**
   * Reset a `timedOut` status back to `idle` so the UI stops surfacing the timeout banner.
   * No-op while the instance is still `pending` or already `idle`.
   */
  public clear(instanceId: string): void {
    if (this.statuses.get(instanceId) !== 'timedOut') {
      return;
    }
    this.entries.delete(instanceId);
    this.setStatus(instanceId, null);
  }

  @action
  private setStatus(instanceId: string, status: GraphAuthStatus | null): void {
    if (status === null) {
      this.statuses.delete(instanceId);
    } else {
      this.statuses.set(instanceId, status);
    }
  }

  private scheduleNext(instanceId: string): void {
    const entry = this.entries.get(instanceId);
    if (!entry) {
      return;
    }
    entry.timer = setTimeout(() => {
      void this.tick(instanceId);
    }, entry.delay);
  }

  private async tick(instanceId: string): Promise<void> {
    const entry = this.entries.get(instanceId);
    if (!entry) {
      return;
    }
    entry.timer = null;

    if (Date.now() - entry.startedAt >= TIMEOUT_MS) {
      this.fail(instanceId, new GraphAuthTimeoutError());
      return;
    }

    try {
      await entry.check();
      this.succeed(instanceId);
    } catch (error) {
      if (await isGraphAuthPendingError(error)) {
        entry.delay = Math.min(entry.delay * BACKOFF_FACTOR, MAX_DELAY_MS);
        this.scheduleNext(instanceId);
      } else {
        this.fail(instanceId, error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  private succeed(instanceId: string): void {
    const entry = this.entries.get(instanceId);
    if (!entry) {
      return;
    }
    const waiters = entry.waiters;
    this.entries.delete(instanceId);
    this.setStatus(instanceId, null);
    for (const waiter of waiters) {
      waiter.resolve();
    }
  }

  private fail(instanceId: string, error: Error): void {
    const entry = this.entries.get(instanceId);
    if (!entry) {
      return;
    }
    const waiters = entry.waiters;
    this.entries.delete(instanceId);
    this.setStatus(instanceId, 'timedOut');
    for (const waiter of waiters) {
      waiter.reject(error);
    }
  }
}

export const graphAuthStore = new GraphAuthStore();
